import { useEffect, useState, useCallback, useRef, useMemo, memo } from 'react';
import Map, { Marker, Popup } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import { Card, Text, Badge, Group, Stack, Center, Loader } from '@mantine/core';
import { MapPin } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useStore } from '../../store/useStore';
import type { Device, DeviceState } from '../../types';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

// Default view when no devices (Washington DC)
const DEFAULT_VIEW = { longitude: -77.0369, latitude: 38.9072, zoom: 10 };

interface NodeMapViewProps {
  onDeviceClick?: (device: Device) => void;
}

function getStateColor(state: DeviceState): string {
  switch (state) {
    case 'active':
      return '#22c55e'; // green
    case 'configured':
      return '#3b82f6'; // blue
    case 'pending':
      return '#f59e0b'; // yellow/orange
    case 'inactive':
      return '#6b7280'; // gray
    case 'disabled':
      return '#ef4444'; // red
    default:
      return '#6b7280'; // gray
  }
}

function getStateBadgeColor(state: DeviceState): string {
  switch (state) {
    case 'active':
      return 'green';
    case 'configured':
      return 'blue';
    case 'pending':
      return 'yellow';
    case 'inactive':
      return 'gray';
    case 'disabled':
      return 'red';
    default:
      return 'gray';
  }
}

// Compute bounds from devices - returns LngLatBoundsLike format
// For single point, adds a small buffer to create valid bounds
function computeBounds(devicesWithLocation: Device[]): [[number, number], [number, number]] | null {
  if (devicesWithLocation.length === 0) return null;

  let minLat = Infinity, maxLat = -Infinity;
  let minLon = Infinity, maxLon = -Infinity;

  for (const d of devicesWithLocation) {
    if (d.location) {
      minLat = Math.min(minLat, d.location.lat);
      maxLat = Math.max(maxLat, d.location.lat);
      minLon = Math.min(minLon, d.location.lon);
      maxLon = Math.max(maxLon, d.location.lon);
    }
  }

  // For single point or very close points, add buffer to create valid bounds
  const latBuffer = maxLat === minLat ? 0.01 : 0;
  const lonBuffer = maxLon === minLon ? 0.01 : 0;

  return [
    [minLon - lonBuffer, minLat - latBuffer], // southwest
    [maxLon + lonBuffer, maxLat + latBuffer], // northeast
  ];
}

// Memoized marker component to prevent unnecessary re-renders
const DeviceMarker = memo(function DeviceMarker({
  device,
  onMarkerClick,
}: {
  device: Device;
  onMarkerClick: (e: { originalEvent: MouseEvent }, device: Device) => void;
}) {
  const color = getStateColor(device.state);

  return (
    <Marker
      longitude={device.location!.lon}
      latitude={device.location!.lat}
      anchor="center"
      onClick={(e) => onMarkerClick(e, device)}
    >
      <div
        style={{
          backgroundColor: color,
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'pointer',
        }}
      />
    </Marker>
  );
});

export function NodeMapView({ onDeviceClick }: NodeMapViewProps) {
  // Subscribe to store with shallow comparison for devices
  const devices = useStore((state) => state.devices);
  const devicesLoading = useStore((state) => state.devicesLoading);

  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [viewState, setViewState] = useState(DEFAULT_VIEW);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const hasInitializedRef = useRef(false);
  const pendingFitBoundsRef = useRef<[[number, number], [number, number]] | null>(null);

  // Filter to devices with location - memoized
  const devicesWithLocation = useMemo(
    () => devices.filter(
      (d) => d.location !== undefined && d.location !== null && d.location.lat !== null && d.location.lon !== null
    ),
    [devices]
  );

  // Robust fitBounds function that retries if map not ready
  const fitToBounds = useCallback((bounds: [[number, number], [number, number]]) => {
    const map = mapRef.current;
    if (!map) {
      // Store for later if map not ready
      pendingFitBoundsRef.current = bounds;
      return;
    }

    try {
      map.fitBounds(bounds, {
        padding: { top: 60, bottom: 60, left: 60, right: 60 },
        maxZoom: 15,
        duration: 1000,
      });
      hasInitializedRef.current = true;
      pendingFitBoundsRef.current = null;
    } catch (e) {
      // If fitBounds fails, store for retry
      pendingFitBoundsRef.current = bounds;
    }
  }, []);

  // Effect: When map loads, check for pending fitBounds
  useEffect(() => {
    if (mapLoaded && pendingFitBoundsRef.current) {
      const bounds = pendingFitBoundsRef.current;
      // Small delay to ensure map is fully initialized
      const timer = setTimeout(() => {
        fitToBounds(bounds);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mapLoaded, fitToBounds]);

  // Effect: Fit bounds on initial data load
  useEffect(() => {
    // Skip if already initialized or no data
    if (hasInitializedRef.current || devicesWithLocation.length === 0) return;

    const bounds = computeBounds(devicesWithLocation);
    if (!bounds) return;

    if (mapLoaded) {
      // Map is ready, fit immediately (with small delay for safety)
      const timer = setTimeout(() => {
        fitToBounds(bounds);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Map not ready yet, store bounds for when it loads
      pendingFitBoundsRef.current = bounds;
    }
  }, [mapLoaded, devicesWithLocation, fitToBounds]);

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  const handleMarkerClick = useCallback(
    (e: { originalEvent: MouseEvent }, device: Device) => {
      e.originalEvent.stopPropagation();
      setSelectedDevice(device);
    },
    []
  );

  const handleMapClick = useCallback(() => {
    setSelectedDevice(null);
  }, []);

  const handleMove = useCallback((evt: { viewState: typeof viewState }) => {
    setViewState(evt.viewState);
  }, []);

  const formatLastHeartbeat = useCallback((timestamp?: string) => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  }, []);

  // Update selected device if it changed in the store
  useEffect(() => {
    if (selectedDevice) {
      const updated = devicesWithLocation.find(d => d.sensor_id === selectedDevice.sensor_id);
      if (updated && updated !== selectedDevice) {
        setSelectedDevice(updated);
      }
    }
  }, [devicesWithLocation, selectedDevice]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Card.Section>
        <div style={{ height: '450px', width: '100%', position: 'relative' }}>
          {devicesLoading && !hasInitializedRef.current && (
            <Center
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
                borderRadius: '8px',
              }}
            >
              <Loader size="lg" />
            </Center>
          )}
          <Map
            ref={mapRef}
            {...viewState}
            onMove={handleMove}
            onLoad={handleMapLoad}
            mapboxAccessToken={MAPBOX_TOKEN}
            style={{ width: '100%', height: '100%', borderRadius: '8px' }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            onClick={handleMapClick}
            reuseMaps
          >
            {devicesWithLocation.map((device) => (
              <DeviceMarker
                key={device.sensor_id}
                device={device}
                onMarkerClick={handleMarkerClick}
              />
            ))}

            {selectedDevice && selectedDevice.location && (
              <Popup
                longitude={selectedDevice.location.lon}
                latitude={selectedDevice.location.lat}
                anchor="bottom"
                onClose={() => setSelectedDevice(null)}
                closeOnClick={false}
                offset={12}
              >
                <Stack gap="xs" style={{ minWidth: '220px', padding: '4px' }}>
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" fw={600}>
                      {selectedDevice.sensor_name || selectedDevice.sensor_id}
                    </Text>
                    <Badge
                      color={getStateBadgeColor(selectedDevice.state)}
                      size="sm"
                    >
                      {selectedDevice.state.toUpperCase()}
                    </Badge>
                  </Group>

                  <Text size="xs" ff="monospace" c="dimmed">
                    {selectedDevice.mac_address}
                  </Text>

                  <Text size="xs" c="dimmed">
                    Last heartbeat: {formatLastHeartbeat(selectedDevice.last_heartbeat)}
                  </Text>

                  <Group gap="xs" mt="xs">
                    <MapPin size={12} />
                    <Text size="xs" c="dimmed">
                      {selectedDevice.location.lat.toFixed(5)},{' '}
                      {selectedDevice.location.lon.toFixed(5)}
                    </Text>
                  </Group>

                  {onDeviceClick && (
                    <Text
                      size="xs"
                      c="blue"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      mt="xs"
                      onClick={() => {
                        onDeviceClick(selectedDevice);
                        setSelectedDevice(null);
                      }}
                    >
                      View Details
                    </Text>
                  )}
                </Stack>
              </Popup>
            )}
          </Map>
        </div>
      </Card.Section>

      {devicesWithLocation.length === 0 && !devicesLoading && (
        <Text c="dimmed" size="sm" ta="center" mt="md">
          No devices with location data available
        </Text>
      )}

      {devicesWithLocation.length > 0 && (
        <Group gap="lg" mt="md" justify="center">
          <Group gap={6}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#22c55e',
              }}
            />
            <Text size="xs">Active</Text>
          </Group>
          <Group gap={6}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
              }}
            />
            <Text size="xs">Configured</Text>
          </Group>
          <Group gap={6}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
              }}
            />
            <Text size="xs">Pending</Text>
          </Group>
          <Group gap={6}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#6b7280',
              }}
            />
            <Text size="xs">Inactive</Text>
          </Group>
        </Group>
      )}
    </Card>
  );
}
